import React, { useEffect } from "react";
import { Alert, Descriptions, Form, Input, List, Modal, Radio, Space, Tag, Typography } from "antd";

const { Text } = Typography;
const { TextArea } = Input;

const safeArray = (value) => (Array.isArray(value) ? value : []);

const ImpactSummary = ({ impact }) => {
    const affectedInvoices = safeArray(impact?.affected_invoices);
    const affectedLineItems = safeArray(impact?.affected_line_items);
    const affectedFlightIds = safeArray(impact?.affected_flight_ids);
    const completedBatches = safeArray(impact?.completed_batches);
    const profitReportItemsCount = Number(impact?.profit_report_items_count || 0);

    return (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="受影响发票">{affectedInvoices.length} 张</Descriptions.Item>
                <Descriptions.Item label="受影响明细">{affectedLineItems.length} 条</Descriptions.Item>
                <Descriptions.Item label="受影响航段">{affectedFlightIds.length} 个</Descriptions.Item>
                <Descriptions.Item label="已完成批次">{completedBatches.length} 个</Descriptions.Item>
                <Descriptions.Item label="利润报表项" span={2}>{profitReportItemsCount} 条</Descriptions.Item>
            </Descriptions>

            {completedBatches.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message={`此操作将影响 ${completedBatches.length} 个已完成批次`}
                    description="作废后，下游批次汇总与利润报表会按后端生命周期口径排除旧数据。"
                />
            )}

            {(affectedInvoices.length > 0 || affectedLineItems.length > 0) && (
                <List
                    size="small"
                    bordered
                    dataSource={[
                        ...affectedInvoices.slice(0, 3).map((item) => ({ type: "发票", label: item.invoice_number || item.id })),
                        ...affectedLineItems.slice(0, 3).map((item) => ({ type: "明细", label: item.cost_type ? `${item.cost_type} / ${item.amount ?? item.id}` : item.id })),
                    ]}
                    renderItem={(item) => (
                        <List.Item>
                            <Space>
                                <Tag>{item.type}</Tag>
                                <Text>{item.label}</Text>
                            </Space>
                        </List.Item>
                    )}
                />
            )}
        </Space>
    );
};

const VoidConfirmModal = ({
    open,
    title = "作废确认",
    targetLabel,
    impact,
    impactLoading = false,
    confirmText = "确认作废",
    submitting = false,
    onCancel,
    onConfirm,
}) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (open) {
            form.resetFields();
        }
    }, [form, open]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            await onConfirm?.(values.reason.trim(), values.mode || "copy");
        } catch {
            return;
        }
    };

    return (
        <Modal
            title={title}
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            okText={confirmText}
            cancelText="取消"
            okButtonProps={{ danger: true, loading: submitting }}
            confirmLoading={submitting}
            width={680}
            destroyOnClose
        >
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <Alert
                    type="error"
                    showIcon
                    message="作废是不可逆操作"
                    description={targetLabel ? `目标：${targetLabel}。作废后旧数据会进入不可编辑状态，并由后端审计链记录。` : "作废后旧数据会进入不可编辑状态，并由后端审计链记录。"}
                />

                {impactLoading ? (
                    <Alert type="info" showIcon message="正在评估作废影响..." />
                ) : (
                    <ImpactSummary impact={impact} />
                )}

                <Form form={form} layout="vertical" requiredMark={false} initialValues={{ mode: "copy" }}>
                    <Form.Item label="作废后处理方式" name="mode" rules={[{ required: true, message: "请选择作废后处理方式" }]}>
                        <Radio.Group style={{ width: "100%" }}>
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                <Radio value="copy" style={{ display: "block" }}>
                                    <Space direction="vertical" size={2}>
                                        <Text strong>复制并修改（推荐）</Text>
                                        <Text type="secondary">作废后复制原数据，您只需修改错误字段</Text>
                                    </Space>
                                </Radio>
                                <Radio value="reparse" style={{ display: "block" }}>
                                    <Space direction="vertical" size={2}>
                                        <Text strong>重新解析</Text>
                                        <Text type="secondary">完全重新走 OCR 和解析流程</Text>
                                    </Space>
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="作废原因"
                        name="reason"
                        rules={[
                            { required: true, message: "请输入作废原因" },
                            {
                                validator: (_, value) => {
                                    if (!value || value.trim().length >= 5) return Promise.resolve();
                                    return Promise.reject(new Error("作废原因至少 5 个字符"));
                                },
                            },
                        ]}
                    >
                        <TextArea rows={4} maxLength={500} showCount placeholder="请说明作废原因，例如：账单字段错误，需要创建新单修正" />
                    </Form.Item>
                </Form>
            </Space>
        </Modal>
    );
};

export default VoidConfirmModal;
